import {
  Component,
  ElementRef,
  inject,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TerminalService, TerminalSession } from '../services/terminal.service';

@Component({
  selector: 'app-terminal',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './terminal.component.html',
  styleUrl: './terminal.component.css',
})
export class TerminalComponent implements OnInit, OnDestroy {
  @Input({ required: true }) clientId!: string;
  @ViewChild('outputEl') outputEl!: ElementRef<HTMLElement>;

  private readonly terminalService = inject(TerminalService);

  private session: TerminalSession | null = null;
  private senderId = crypto.randomUUID();
  private commandSequence = 0;

  lines: { content: string; isHtml: boolean }[] = [];
  currentInput = '';
  isConnected = false;
  hasError = false;

  ngOnInit(): void {
    this.connect();
  }

  ngOnDestroy(): void {
    this.session?.close();
  }

  private connect(): void {
    this.hasError = false;
    this.isConnected = false;
    this.senderId = crypto.randomUUID();
    this.commandSequence = 0;

    this.session = this.terminalService.openStream(
      this.clientId,
      this.senderId,
      (data) => {
        this.isConnected = true;
        this.appendLine(data);
      },
      () => {
        this.isConnected = false;
        this.hasError = true;
      }
    );

    // mark connected optimistically — server confirms on first message
    setTimeout(() => {
      if (!this.hasError) this.isConnected = true;
    }, 500);
  }

  sendCommand(): void {
    const command = this.currentInput.trim();
    if (!command) return;

    this.appendLine(`$ ${command}`);
    this.currentInput = '';
    this.commandSequence++;

    this.terminalService
      .sendCommand(this.clientId, this.senderId, command, this.commandSequence)
      .subscribe({
        error: () => this.appendLine('[error: failed to send command]'),
      });
  }

  reconnect(): void {
    this.session?.close();
    this.lines = [];
    this.connect();
  }

  private appendLine(text: string): void {
    const isHtml = text.trimStart().startsWith('<');
    this.lines.push({ content: text, isHtml });
    setTimeout(() => {
      const el = this.outputEl?.nativeElement;
      if (el) el.scrollTop = el.scrollHeight;
    });
  }
}
